/**
 * Client Detection Utility
 *
 * Detects whether the current client is frontend, mobile webview, or white-label.
 * Used for analytics deduplication and client-specific behavior.
 */

export type ClientType = 'frontend' | 'mobile' | 'webview' | 'white-label'

/**
 * Detect the type of client accessing the application.
 *
 * Detection priority:
 * 1. URL params (client=mobile, source=mobile_app) - explicit mobile app signals
 * 2. ReactNativeWebView global - indicates iOS/Android webview
 * 3. User agent patterns - PassTicketsApp or ReactNative identifiers
 * 4. Default to 'frontend' for standard web browsers
 */
export function detectClient(): ClientType {
  if (typeof window === 'undefined') return 'frontend'

  // Check URL params first (mobile app passes these)
  const params = new URLSearchParams(window.location.search)
  if (params.get('client') === 'mobile' || params.get('source') === 'mobile_app') return 'webview'

  // Check if ReactNativeWebView is available (mobile webview)
  if ((window as any).ReactNativeWebView) return 'webview'

  // Check user agent for mobile app
  const ua = navigator.userAgent
  if (ua.includes('PassTicketsApp') || ua.includes('ReactNative')) return 'webview'

  // Check for white-label domain (future)
  // if (isWhiteLabelDomain(window.location.hostname)) return 'white-label'

  return 'frontend'
}

/**
 * Get or create a client session ID.
 *
 * This ID is:
 * - Shared between webview and mobile app (passed via URL param)
 * - Persisted in sessionStorage for the duration of the browser session
 * - Used for analytics deduplication between web and native tracking
 */
export function getClientSessionId(): string {
  // Use sessionStorage for per-session ID, shared between webview and mobile
  const key = 'analytics_client_session_id'

  // Check URL params first - mobile app may pass session ID
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const mobileSessionId = params.get('client_session_id')
    if (mobileSessionId) {
      sessionStorage.setItem(key, mobileSessionId)
      return mobileSessionId
    }
  }

  let sessionId = sessionStorage.getItem(key)
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    sessionStorage.setItem(key, sessionId)
  }
  return sessionId
}
